import { create } from "zustand";

/**
 * Info question: `code` is what the API stores (ehrPortalQuestion Description); `question` is
 * display-only. `options` are the saved answer values, so they must not change.
 */
export const INFO_QUESTIONS: { code: string; question: string; options: string[] }[] = [
  { code: "NumberOfChildren", question: "Number of children?", options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] },
  { code: "ExerciseFrequency", question: "Exercise frequency?", options: ["Never", "Rarely", "Occasionally", "Moderately", "Regularly"] },
  { code: "ExerciseIntensity", question: "Exercise intensity level?", options: ["Low", "Medium", "High", "Competition"] },
  { code: "SufficientRest", question: "Sufficient rest?", options: ["Never", "Rarely", "Occasionally", "Sometimes", "Often"] },
  { code: "HoursOfSleep", question: "Hours of sleep?", options: ["Less than 6", "6 to 8", "8 to 10", "More than 10"] },
  { code: "WellBalancedDiet", question: "Well balanced diet?", options: ["Never", "Rarely", "Sometimes", "Often"] },
  { code: "Smoke", question: "Do you smoke?", options: ["No", "Occasionally", "1 to 2 packs", "2 to 3 packs", "4 or more"] },
  { code: "CaffeinatedDrinks", question: "Do you drink caffeinated beverages?", options: ["No", "Occasionally", "1 to 2 Drinks", "2 to 3 Drinks", "4 or more"] },
  { code: "AlcoholicDrinks", question: "Do you drink alcoholic beverages?", options: ["No", "Occasionally", "1 to 2 Drinks", "2 to 3 Drinks", "4 or more"] },
  { code: "StreetDrugs", question: "Have you ever used street drugs?", options: ["Yes", "No"] },
];

/**
 * Conditions: Present/Past multi-select (same as Vue). These strings are saved as-is to
 * ehrPortalCondition.Description and matched by string on load and in udp_ehrPortalCondition_Update,
 * so they are keys: never reword them. Show `conditionDisplayLabel(key)` instead.
 */
export const CONDITION_LABELS = [
  "Fatigue", "Seizures", "Fever", "Depression", "Chills", "Dizziness",
  "Tremors", "Memory Loss", "Night Sweats", "Hot Joints", "Fainting",
  "Muscle Weakness", "Joint Swelling", "Loss of Coordination", "Lumps/Masses", "Difficulty of Speech",
];

/** Sentence-case labels for conditions whose saved key is title case (DESIGN.md). */
const CONDITION_DISPLAY_LABELS: Record<string, string> = {
  "Memory Loss": "Memory loss",
  "Night Sweats": "Night sweats",
  "Hot Joints": "Hot joints",
  "Muscle Weakness": "Muscle weakness",
  "Joint Swelling": "Joint swelling",
  "Loss of Coordination": "Loss of coordination",
  "Lumps/Masses": "Lumps/masses",
  "Difficulty of Speech": "Difficulty of speech",
};

export function conditionDisplayLabel(key: string): string {
  return CONDITION_DISPLAY_LABELS[key] ?? key;
}

/** Medications "Have you ever taken": `code` is saved, `question` is display-only. */
export const MEDICINE_QUESTIONS: { code: string; question: string }[] = [
  { code: "Insulin", question: "Insulin" },
  { code: "Cortisone", question: "Cortisone" },
  { code: "MaleFemaleHormones", question: "Male/female hormones" },
  { code: "BloodPressure", question: "Blood pressure" },
];

/** Additional health: `code` is saved, `question` (Yes/No or date) is display-only. */
export const MEDICINE2_QUESTIONS: { code: string; question: string; isDate?: boolean }[] = [
  { code: "Medications", question: "Are you taking any medications?", isDate: false },
  { code: "NutritionalSupplements", question: "Are you taking any nutritional supplement?", isDate: false },
  { code: "Surgeries", question: "Have you had any surgeries?", isDate: false },
  { code: "Hospitalization", question: "Have you any hospitalization?", isDate: false },
  { code: "SignificantPastTrauma", question: "Have you had any significant past trauma?", isDate: false },
  { code: "Pacemaker", question: "Do you have a pacemaker?", isDate: false },
];

export interface HealthHistoryState {
  infoAnswers: Record<string, string>;
  conditions: Record<string, { present: boolean; past: boolean }>;
  medicineAnswers: Record<string, { answer: string; year: string }>;
  medicine2Answers: Record<string, string>;
  setInfoAnswers: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  setConditions: (updater: (prev: Record<string, { present: boolean; past: boolean }>) => Record<string, { present: boolean; past: boolean }>) => void;
  setMedicineAnswers: (updater: (prev: Record<string, { answer: string; year: string }>) => Record<string, { answer: string; year: string }>) => void;
  setMedicine2Answers: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  reset: () => void;
}

const initialConditions = (): Record<string, { present: boolean; past: boolean }> => {
  const init: Record<string, { present: boolean; past: boolean }> = {};
  CONDITION_LABELS.forEach((c) => (init[c] = { present: false, past: false }));
  return init;
};

export const useHealthHistoryStore = create<HealthHistoryState>((set) => ({
  infoAnswers: {},
  conditions: initialConditions(),
  medicineAnswers: {},
  medicine2Answers: {},
  setInfoAnswers: (updater) => set((s) => ({ infoAnswers: updater(s.infoAnswers) })),
  setConditions: (updater) => set((s) => ({ conditions: updater(s.conditions) })),
  setMedicineAnswers: (updater) => set((s) => ({ medicineAnswers: updater(s.medicineAnswers) })),
  setMedicine2Answers: (updater) => set((s) => ({ medicine2Answers: updater(s.medicine2Answers) })),
  reset: () =>
    set({
      infoAnswers: {},
      conditions: initialConditions(),
      medicineAnswers: {},
      medicine2Answers: {},
    }),
}));
